import Vue from 'vue'
import ChunkErrorModal from '../components/ChunkErrorModal.vue'

// 創建自定義彈窗實例來顯示 Chunk 錯誤
let chunkErrorModalInstance = null
let isModalShowing = false

const showChunkErrorModal = () => {
	// 防止重複顯示彈窗
	if (isModalShowing) {
		return Promise.resolve(false)
	}

	return new Promise(resolve => {
		isModalShowing = true

		// 清理現有實例
		if (chunkErrorModalInstance) {
			try {
				chunkErrorModalInstance.$off() // 移除所有事件監聽器
				chunkErrorModalInstance.$destroy()
				if (
					chunkErrorModalInstance.$el &&
					chunkErrorModalInstance.$el.parentNode
				) {
					chunkErrorModalInstance.$el.parentNode.removeChild(
						chunkErrorModalInstance.$el,
					)
				}
			} catch (e) {
				console.warn('清理彈窗實例時出錯:', e)
			}
			chunkErrorModalInstance = null
		}

		const ChunkErrorModalConstructor = Vue.extend(ChunkErrorModal)
		chunkErrorModalInstance = new ChunkErrorModalConstructor({
			propsData: {
				value: true,
			},
		})

		const cleanup = () => {
			try {
				if (chunkErrorModalInstance) {
					chunkErrorModalInstance.$off()
					chunkErrorModalInstance.$destroy()
					if (
						chunkErrorModalInstance.$el &&
						chunkErrorModalInstance.$el.parentNode
					) {
						chunkErrorModalInstance.$el.parentNode.removeChild(
							chunkErrorModalInstance.$el,
						)
					}
					chunkErrorModalInstance = null
				}
			} catch (e) {
				console.warn('清理彈窗實例時出錯:', e)
			}
			isModalShowing = false
		}

		chunkErrorModalInstance.$on('onConfirm', () => {
			cleanup()
			resolve(true)
		})

		// 添加錯誤處理
		chunkErrorModalInstance.$on('hook:errorCaptured', error => {
			console.error('彈窗組件錯誤:', error)
			cleanup()
			resolve(false)
		})

		try {
			chunkErrorModalInstance.$mount()
			document.body.appendChild(chunkErrorModalInstance.$el)
		} catch (e) {
			console.error('創建彈窗時出錯:', e)
			cleanup()
			resolve(false)
		}
	})
}

// 處理 ChunkLoadError 的全域錯誤監聽器
let isHandlingChunkError = false
const handleChunkLoadError = error => {
	// 防止同時處理多個錯誤
	if (isHandlingChunkError) {
		return true
	}

	const chunkFailedMessage = /Loading chunk \d+ failed/
	const chunkFailedMessageChinese = /載入 chunk \d+ 失敗/
	const syntaxErrorFromChunk = /Unexpected token '<'/
	const chunkFilePattern = /\d+\.[a-f0-9]+\.js/

	// 檢查是否為 chunk 相關的語法錯誤（通常是 404 返回 HTML 導致）
	const isChunkSyntaxError =
		error.name === 'SyntaxError' &&
		syntaxErrorFromChunk.test(error.message) &&
		(error.fileName || error.stack || '').match(chunkFilePattern)

	if (
		error.name === 'ChunkLoadError' ||
		chunkFailedMessage.test(error.message) ||
		chunkFailedMessageChinese.test(error.message) ||
		isChunkSyntaxError
	) {
		isHandlingChunkError = true

		// 檢查重載次數限制，防止無限循環
		const reloadCountKey = 'byod_chunkReloadCount'
		const reloadCount = parseInt(
			sessionStorage.getItem(reloadCountKey) || '0',
			10,
		)
		const maxReloadAttempts = 3

		// 顯示用戶友好的錯誤彈窗，讓用戶決定是否重新載入
		console.warn(
			`檢測到資源載入錯誤 (第 ${reloadCount + 1}/${maxReloadAttempts} 次)`,
			error,
		)

		if (reloadCount >= maxReloadAttempts) {
			console.error(
				`ChunkLoadError 重載次數已達上限 (${maxReloadAttempts} 次)，停止自動重載`,
				error,
			)
			// 清除計數器，等待用戶手動刷新
			sessionStorage.removeItem(reloadCountKey)
		} else {
			// 增加重載計數
			sessionStorage.setItem(reloadCountKey, String(reloadCount + 1))
		}

		// 顯示自定義彈窗讓用戶決定
		showChunkErrorModal()
			.then(shouldReload => {
				if (shouldReload) {
					sessionStorage.removeItem(reloadCountKey)
					window.location.reload()
				}
			})
			.finally(() => {
				// 重置處理狀態，允許後續錯誤處理
				setTimeout(() => {
					isHandlingChunkError = false
				}, 1000)
			})

		return true
	}
	return false
}

// 初始化錯誤處理器
export const initChunkErrorHandler = () => {
	// Vue 全域錯誤處理器
	Vue.config.errorHandler = (error, vm, info) => {
		console.error('Vue 全域錯誤:', error, info)

		// 先嘗試處理 ChunkLoadError
		if (handleChunkLoadError(error)) {
			return
		}

		// 其他錯誤的處理邏輯可以在這裡添加
	}

	// 全域 unhandledrejection 事件監聽器（處理 Promise 拒絕）
	window.addEventListener('unhandledrejection', event => {
		console.error('未處理的 Promise 拒絕:', event.reason)

		// 嘗試處理 ChunkLoadError
		if (handleChunkLoadError(event.reason)) {
			event.preventDefault() // 阻止默認的錯誤處理
			return
		}
	})

	// 全域 error 事件監聽器（處理一般錯誤）
	window.addEventListener('error', event => {
		console.error('全域錯誤事件:', event.error)

		// 嘗試處理 ChunkLoadError
		if (handleChunkLoadError(event.error)) {
			event.preventDefault()
			return
		}
	})
}

export default {
	initChunkErrorHandler,
	handleChunkLoadError,
	showChunkErrorModal,
}
